import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import * as XLSX from 'xlsx';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

/**
 * Lista de Parceiros -- mostra os 5 ultimos cadastros alterados (GPL_DTALTERACAO DESC) e
 * permite pesquisar. "Cadastrar" abre o Cadastro de Parceiros; ao salvar, o
 * gateway volta para esta lista. Usa o mesmo CSS da Lista de Produtos para
 * manter o layout identico.
 */
@Component({
  selector: 'app-parceiros-lista',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent],
  templateUrl: './parceiros-lista.component.html',
  styleUrl: '../produtos-lista/produtos-lista.component.css'
})
export class ParceirosListaComponent implements OnInit {
  @Output() novoParceiro = new EventEmitter<void>();
  @Output() editarParceiro = new EventEmitter<{ doc: string; codparc: string }>();
  /** Cliente do Aerosys ainda nao cadastrado no Sankhya: abre o Cadastro de Parceiros novo ja com o documento. */
  @Output() cadastrarDoAerosys = new EventEmitter<{ doc: string; nome: string }>();

  readonly limiteUltimos = 5;

  parceiros: any[] = [];
  termoBusca = '';
  carregando = false;
  erro = '';
  linhaSelecionada: any = null;

  private timerBusca: any = null;
  private seq = 0;

  constructor(private sankhyaGateway: SankhyaGatewayService) {}

  ngOnInit(): void {
    void this.carregar();
  }

  onNovo(): void {
    this.novoParceiro.emit();
  }

  onEditar(p: any): void {
    const doc = String(p.cgc_cpf || '').replace(/\D/g, '');
    if (!doc) {
      alert('Este parceiro não possui CPF/CNPJ cadastrado e não pode ser aberto em edição.');
      return;
    }
    this.editarParceiro.emit({ doc, codparc: p.codparc });
  }

  /** Atualizar: carrega TODOS os cadastros, paginados (14 por pagina). */
  onAtualizar(): void {
    this.modoTodos = true;
    this.paginaAtual = 1;
    void this.carregar();
  }

  onTermoBuscaChange(): void {
    if (this.timerBusca) clearTimeout(this.timerBusca);
    this.timerBusca = setTimeout(() => {
      this.paginaAtual = 1;
      void this.carregar(false);
    }, 350);
  }

  // Paginacao (so no modo "todos"; o inicial mostra apenas os ultimos 10)
  modoTodos = false;
  paginado = false;
  readonly itensPorPagina = 17;
  paginaAtual = 1;
  totalRegistros = 0;

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.totalRegistros / this.itensPorPagina));
  }

  get paginas(): number[] {
    const r: number[] = [];
    for (let i = Math.max(1, this.paginaAtual - 2); i <= Math.min(this.totalPaginas, this.paginaAtual + 2); i++) r.push(i);
    return r;
  }

  irParaPagina(p: number): void {
    if (p < 1 || p > this.totalPaginas || p === this.paginaAtual) return;
    this.paginaAtual = p;
    void this.carregar();
  }

  async carregar(mostrarCarregando = true): Promise<void> {
    const seq = ++this.seq;
    if (mostrarCarregando) this.carregando = true;
    this.erro = '';
    // Pesquisando tambem entra no modo paginado.
    const paginado = this.modoTodos || this.termoBusca.trim().length > 0;
    try {
      const res: any = await firstValueFrom(
        this.sankhyaGateway.listarParceiros(this.termoBusca.trim(), this.limiteUltimos,
          paginado ? this.paginaAtual : 0, this.itensPorPagina));
      if (seq !== this.seq) return;
      if (res?.sucesso) {
        this.parceiros = res.parceiros || [];
        this.totalRegistros = paginado ? (res.total ?? this.parceiros.length) : this.parceiros.length;
        this.paginado = paginado;
      } else {
        this.parceiros = [];
        this.erro = res?.erro || 'Não foi possível carregar os parceiros.';
      }
    } catch (e: any) {
      if (seq !== this.seq) return;
      this.parceiros = [];
      this.erro = 'Não foi possível carregar os parceiros.';
    } finally {
      if (seq === this.seq) this.carregando = false;
    }
  }

  // ── Funções: exportar Excel/CSV e copiar para a área de transferência ──
  funcoesMenuAberto = false;
  exportando = false;
  exportandoPct = 0;
  avisoFuncoes = '';
  avisoErro = false;
  private timerAviso: any = null;

  toggleFuncoesMenu(event?: Event): void {
    event?.stopPropagation();
    this.funcoesMenuAberto = !this.funcoesMenuAberto;
  }

  @HostListener('document:click')
  fecharMenus(): void {
    this.funcoesMenuAberto = false;
  }

  private mostrarAviso(msg: string, erro = false): void {
    this.avisoFuncoes = msg;
    this.avisoErro = erro;
    if (this.timerAviso) clearTimeout(this.timerAviso);
    this.timerAviso = setTimeout(() => (this.avisoFuncoes = ''), 4000);
  }

  private readonly colunasExport = ['Código', 'Status', 'Nome', 'Tipo', 'Tipo Cadastro', 'CPF/CNPJ', 'Razão Social', 'Cidade', 'UF', 'Cadastro', 'Alteração'];

  /** Busca TODOS os parceiros do filtro atual (todas as páginas de 200). */
  private async coletarParaExportar(): Promise<any[]> {
    // Modo inicial (últimos alterados): exporta exatamente o que está na tela.
    if (!this.paginado) {
      this.exportandoPct = 100;
      return [...this.parceiros];
    }
    // Modo paginado (Atualizar e/ou pesquisa): todas as páginas do filtro atual.
    const termo = this.termoBusca.trim();
    const todos: any[] = [];
    let pagina = 1;
    let total = 0;
    do {
      const res: any = await firstValueFrom(this.sankhyaGateway.listarParceiros(termo, this.limiteUltimos, pagina, 200));
      if (!res?.sucesso) throw new Error(res?.erro || 'Erro ao carregar os parceiros.');
      total = res.total ?? 0;
      const itens = res.parceiros || [];
      todos.push(...itens);
      this.exportandoPct = total > 0 ? Math.min(100, Math.round((todos.length / total) * 100)) : 100;
      pagina++;
      if (!itens.length) break;
    } while (todos.length < total);
    return todos;
  }

  private linhasExport(itens: any[]): string[][] {
    return itens.map(p => [
      String(p.codparc ?? ''),
      p.ativo === 'N' ? 'Inativo' : 'Ativo',
      p.nomeparc || '',
      this.tipoPessoa(p) === '—' ? '' : this.tipoPessoa(p),
      this.tipoCadastro(p) === '—' ? '' : this.tipoCadastro(p),
      this.documento(p) === '—' ? '' : this.documento(p),
      p.razaosocial || '',
      String(p.nomecid || '').toUpperCase(),
      p.siglauf || '',
      p.dtcad || '',
      p.dtalteracao || ''
    ]);
  }

  private nomeArquivo(ext: string): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `parceiros_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}.${ext}`;
  }

  private baixar(blob: Blob, nome: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    a.click();
    URL.revokeObjectURL(url);
  }

  private async executarExport(acao: (linhas: string[][]) => Promise<string> | string): Promise<void> {
    if (this.exportando) return;
    this.funcoesMenuAberto = false;
    this.exportando = true;
    this.exportandoPct = 0;
    this.avisoFuncoes = '';
    try {
      const itens = await this.coletarParaExportar();
      this.mostrarAviso(await acao(this.linhasExport(itens)));
    } catch (err: any) {
      this.mostrarAviso(err?.message ?? 'Erro ao exportar os dados.', true);
    } finally {
      this.exportando = false;
    }
  }

  exportarExcel(): void {
    void this.executarExport(linhas => {
      const ws = XLSX.utils.aoa_to_sheet([this.colunasExport, ...linhas]);
      ws['!cols'] = this.colunasExport.map((_, i) => ({ wch: i === 2 || i === 6 ? 40 : 16 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Parceiros');
      XLSX.writeFile(wb, this.nomeArquivo('xlsx'));
      return `${linhas.length} parceiro(s) exportado(s) para Excel.`;
    });
  }

  exportarCsv(): void {
    void this.executarExport(linhas => {
      const fmt = (v: string): string => (/[;"\n\r]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);
      const csv = [this.colunasExport, ...linhas].map(l => l.map(fmt).join(';')).join('\r\n');
      this.baixar(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }), this.nomeArquivo('csv'));
      return `${linhas.length} parceiro(s) exportado(s) para CSV.`;
    });
  }

  copiarParaAreaDeTransferencia(): void {
    void this.executarExport(async linhas => {
      const texto = [this.colunasExport, ...linhas]
        .map(l => l.map(v => String(v ?? '').replace(/[\t\r\n]+/g, ' ')).join('\t')).join('\r\n');
      await navigator.clipboard.writeText(texto);
      return `Dados copiados para a área de transferência com sucesso! (${linhas.length} parceiro(s))`;
    });
  }


  // ── Função: Validar clientes do Aerosys (existem no Aerosys e faltam/estão incompletos no Sankhya) ──
  vcaAberto = false;
  vcaRodando = false;
  vcaConcluido = false;
  vcaErro = '';
  vcaTotal = 0;
  vcaProcessados = 0;
  vcaOk = 0;
  vcaResultados: any[] = [];
  vcaFiltro: 'TODOS' | 'NAO_CADASTRADO' | 'INCOMPLETO' | 'SEM_DOCUMENTO' = 'TODOS';
  vcaBusca = '';
  vcaLimite = 100;
  vcaExpandido: number | null = null;
  private vcaPararPedido = false;
  private readonly vcaTamanhoLote = 25;

  abrirValidacao(): void {
    this.funcoesMenuAberto = false;
    this.vcaAberto = true;
  }

  fecharValidacao(): void {
    if (this.vcaRodando) this.vcaPararPedido = true;
    this.vcaAberto = false;
  }

  get vcaPct(): number {
    return this.vcaTotal > 0 ? Math.min(100, Math.round((this.vcaProcessados / this.vcaTotal) * 100)) : 0;
  }

  vcaContar(situacao: string): number {
    return this.vcaResultados.filter(r => r.situacao === situacao).length;
  }

  get vcaFiltrados(): any[] {
    const termo = this.vcaBusca.trim().toUpperCase();
    const doc = termo.replace(/\D/g, '');
    return this.vcaResultados.filter(r =>
      (this.vcaFiltro === 'TODOS' || r.situacao === this.vcaFiltro) &&
      (!termo || String(r.nome || '').toUpperCase().includes(termo) || (doc && String(r.doc || '').includes(doc))));
  }

  vcaRotulo(situacao: string): string {
    return situacao === 'NAO_CADASTRADO' ? 'Não cadastrado'
      : situacao === 'INCOMPLETO' ? 'Incompleto'
      : situacao === 'SEM_DOCUMENTO' ? 'Sem CPF/CNPJ' : situacao;
  }

  vcaPararValidacao(): void {
    this.vcaPararPedido = true;
  }

  async iniciarValidacao(): Promise<void> {
    if (this.vcaRodando) return;
    this.vcaRodando = true;
    this.vcaConcluido = false;
    this.vcaPararPedido = false;
    this.vcaErro = '';
    this.vcaResultados = [];
    this.vcaTotal = 0;
    this.vcaProcessados = 0;
    this.vcaOk = 0;
    this.vcaExpandido = null;
    this.vcaLimite = 100;
    this.vcaTipoFoneNaValidacao = this.vcaVerificarTipoFone;
    try {
      const lista: any = await firstValueFrom(this.sankhyaGateway.listarClientesAerosysValidacao());
      if (!lista?.sucesso) throw new Error(lista?.erro || 'Não foi possível listar os cadastros do Aerosys.');
      const ids: number[] = (lista.clientes || []).map((c: any) => Number(c.id)).filter((n: number) => n > 0);
      this.vcaTotal = ids.length;
      for (let i = 0; i < ids.length && !this.vcaPararPedido; i += this.vcaTamanhoLote) {
        const lote = ids.slice(i, i + this.vcaTamanhoLote);
        const res: any = await firstValueFrom(this.sankhyaGateway.validarClientesAerosysLote(lote, this.vcaTipoFoneNaValidacao));
        if (!res?.sucesso) throw new Error(res?.erro || 'Falha ao validar um lote de cadastros.');
        this.vcaOk += Number(res.ok || 0);
        if (Array.isArray(res.resultados) && res.resultados.length) {
          this.vcaResultados = [...this.vcaResultados, ...res.resultados];
        }
        this.vcaProcessados += lote.length;
      }
      this.vcaConcluido = !this.vcaPararPedido;
      if (this.vcaPararPedido) this.vcaErro = 'Validação interrompida. Os resultados abaixo são parciais.';
    } catch (e: any) {
      this.vcaErro = e?.error?.message || e?.message || 'Erro ao validar os cadastros.';
    } finally {
      this.vcaRodando = false;
    }
  }

  vcaCadastrar(r: any): void {
    this.vcaAberto = false;
    this.cadastrarDoAerosys.emit({ doc: r.doc, nome: r.nome });
  }

  // ───────── Correção: inserir no Sankhya o que falta ─────────
  vcaCorrAberto = false;
  vcaCorrFase: 'simulando' | 'resumo' | 'executando' | 'concluido' = 'simulando';
  vcaCorrTitulo = '';
  vcaCorrIds: number[] = [];
  vcaCorrLista: any[] = [];
  vcaCorrErro = '';
  vcaCorrProc = 0;
  /** Categorias aplicadas na correção em andamento (vazio = tudo). */
  private vcaCorrCategorias: string[] = [];
  private readonly vcaCorrLote = 10;

  // ───────── Atualizar por partes (filtro por categoria) ─────────
  readonly vcaCategorias: { chave: string; rotulo: string; dica: string }[] = [
    { chave: 'CADASTRO',     rotulo: 'Cadastrar não existentes', dica: 'Cria no Sankhya os cadastros que existem só no Aerosys (com o 1º endereço)' },
    { chave: 'TIPOS',        rotulo: 'Tipos',                    dica: 'Marca Cliente/Fornecedor/etc. conforme o Aerosys' },
    { chave: 'DADOS',        rotulo: 'Dados cadastrais',         dica: 'Preenche RG, nascimento, mãe, pai, naturalidade, razão social e IE vazios' },
    { chave: 'ENDERECOS',    rotulo: 'Endereços',                dica: 'Cria os endereços que faltam' },
    { chave: 'EMAILS',       rotulo: 'E-mails',                  dica: 'Insere os e-mails ausentes' },
    { chave: 'TELEFONES',    rotulo: 'Telefones',                dica: 'Insere os telefones ausentes' },
    { chave: 'COMPRADORES',  rotulo: 'Compradores',              dica: 'Insere compradores autorizados ausentes' },
    { chave: 'FIADORES',     rotulo: 'Fiadores',                 dica: 'Vincula fiadores ausentes' },
    { chave: 'AERONAVES',    rotulo: 'Aeronaves',                dica: 'Vincula aeronaves ausentes' },
    { chave: 'REFBANCARIAS', rotulo: 'Ref. bancárias',           dica: 'Insere as referências bancárias ausentes' }
  ];
  vcaCatSel = new Set<string>();

  // ───────── Verificação opcional: tipo do telefone (Aerosys x Sankhya) ─────────
  /** Checkbox da tela: desmarcado por padrão. */
  vcaVerificarTipoFone = false;
  /** Valor usado na última validação (revalidações seguem o mesmo critério). */
  private vcaTipoFoneNaValidacao = false;
  vcaTemTipoFone(r: any): boolean { return (r?.categorias || []).includes('TIPOFONE'); }
  get vcaTipoFoneIds(): number[] {
    return this.vcaResultados.filter(r => this.vcaTemTipoFone(r)).map(r => Number(r.pesId)).filter(n => n > 0);
  }

  /** Um a um: atualiza o tipo do telefone daquele cadastro no Sankhya com o do Aerosys. */
  async vcaAtualizarTipoFoneLinha(r: any): Promise<void> {
    this.vcaCorrPreparar('Atualizar tipo do telefone: ' + (r.nome || r.cpfCnpj), [Number(r.pesId)], ['TIPOFONE']);
    await this.vcaCorrRodar(true);
  }

  /** Todos de uma vez: simula, mostra o resumo e só grava após confirmar. */
  async vcaAtualizarTipoFoneTodos(): Promise<void> {
    const ids = this.vcaTipoFoneIds;
    if (!ids.length) return;
    this.vcaCorrPreparar('Atualizar o tipo do telefone de todos os cadastros divergentes', ids, ['TIPOFONE']);
    await this.vcaCorrRodar(false);
  }

  vcaCatToggle(chave: string): void {
    if (this.vcaCatSel.has(chave)) this.vcaCatSel.delete(chave); else this.vcaCatSel.add(chave);
  }

  /** Cadastros (com pendência) que têm diferença naquela categoria. */
  vcaCatContar(chave: string): number {
    return this.vcaResultados.filter(r => (r.categorias || []).includes(chave)).length;
  }

  /** Cadastros que entram na correção por partes: têm diferença em ao menos uma categoria marcada. */
  get vcaCatIds(): number[] {
    if (!this.vcaCatSel.size) return [];
    return this.vcaResultados
      .filter(r => (r.categorias || []).some((c: string) => this.vcaCatSel.has(c)))
      .map(r => Number(r.pesId)).filter(n => n > 0);
  }

  async vcaCorrigirPorPartes(): Promise<void> {
    const ids = this.vcaCatIds;
    if (!ids.length) return;
    const nomes = this.vcaCategorias.filter(c => this.vcaCatSel.has(c.chave)).map(c => c.rotulo).join(', ');
    this.vcaCorrPreparar('Atualizar: ' + nomes, ids, Array.from(this.vcaCatSel));
    await this.vcaCorrRodar(false);
  }

  /** Botão "Corrigir" de uma linha: insere direto o que falta daquele cadastro. */
  async vcaCorrigirLinha(r: any): Promise<void> {
    this.vcaCorrPreparar('Corrigir cadastro: ' + (r.nome || r.cpfCnpj), [Number(r.pesId)]);
    await this.vcaCorrRodar(true);
  }

  /** Botões gerais por situação: simula, mostra o resumo e só insere após confirmar. */
  async vcaCorrigirSituacao(situacao: 'INCOMPLETO' | 'NAO_CADASTRADO'): Promise<void> {
    const ids = this.vcaResultados.filter(r => r.situacao === situacao).map(r => Number(r.pesId)).filter(n => n > 0);
    if (!ids.length) return;
    const titulo = situacao === 'INCOMPLETO' ? 'Corrigir todos os cadastros incompletos' : 'Cadastrar todos os não cadastrados';
    this.vcaCorrPreparar(titulo, ids);
    await this.vcaCorrRodar(false);
  }

  vcaVerSemDocumento(): void {
    this.vcaFiltro = 'SEM_DOCUMENTO';
    this.vcaLimite = 100;
  }

  private vcaCorrPreparar(titulo: string, ids: number[], categorias: string[] = []): void {
    this.vcaCorrCategorias = categorias;
    this.vcaCorrTitulo = titulo;
    this.vcaCorrIds = ids;
    this.vcaCorrLista = [];
    this.vcaCorrErro = '';
    this.vcaCorrProc = 0;
    this.vcaCorrAberto = true;
  }

  vcaCorrFechar(): void {
    if (this.vcaCorrFase === 'simulando' || this.vcaCorrFase === 'executando') return;
    this.vcaCorrAberto = false;
  }

  async vcaCorrConfirmar(): Promise<void> {
    await this.vcaCorrRodar(true);
  }

  private async vcaCorrRodar(executar: boolean): Promise<void> {
    this.vcaCorrFase = executar ? 'executando' : 'simulando';
    this.vcaCorrLista = [];
    this.vcaCorrErro = '';
    this.vcaCorrProc = 0;
    const processados: number[] = [];
    try {
      for (let i = 0; i < this.vcaCorrIds.length; i += this.vcaCorrLote) {
        const lote = this.vcaCorrIds.slice(i, i + this.vcaCorrLote);
        const res: any = await firstValueFrom(this.sankhyaGateway.corrigirClientesAerosysLote(lote, executar, this.vcaCorrCategorias));
        if (!res?.sucesso) throw new Error(res?.erro || 'Falha ao processar um lote.');
        this.vcaCorrLista = [...this.vcaCorrLista, ...(res.resultados || [])];
        processados.push(...lote);
        this.vcaCorrProc += lote.length;
      }
    } catch (e: any) {
      this.vcaCorrErro = (e?.error?.message || e?.message || 'Erro ao processar a correção.')
        + (executar && processados.length ? ' Os cadastros já processados foram mantidos.' : '');
    }
    this.vcaCorrFase = executar ? 'concluido' : 'resumo';
    if (executar && processados.length) await this.vcaRevalidar(processados);
  }

  /** Revalida só os cadastros tocados e atualiza a lista (os que ficaram completos saem). */
  private async vcaRevalidar(ids: number[]): Promise<void> {
    try {
      const novos: any[] = [];
      let completos = 0;
      for (let i = 0; i < ids.length; i += this.vcaTamanhoLote) {
        const lote = ids.slice(i, i + this.vcaTamanhoLote);
        const res: any = await firstValueFrom(this.sankhyaGateway.validarClientesAerosysLote(lote, this.vcaTipoFoneNaValidacao));
        if (!res?.sucesso) return;
        completos += Number(res.ok || 0);
        novos.push(...(res.resultados || []));
      }
      const tocados = new Set(ids.map(Number));
      this.vcaResultados = [...this.vcaResultados.filter(r => !tocados.has(Number(r.pesId))), ...novos];
      this.vcaOk += completos;
      this.vcaExpandido = null;
    } catch {
      // a lista fica como estava; "Validar novamente" atualiza
    }
  }

  get vcaCorrClientes(): number {
    return this.vcaCorrLista.length;
  }

  private vcaCorrAcoes(status: string): any[] {
    const out: any[] = [];
    for (const c of this.vcaCorrLista) for (const a of (c.acoes || [])) if (a.status === status) out.push(a);
    return out;
  }

  vcaCorrContar(status: string): number {
    return this.vcaCorrAcoes(status).length;
  }

  /** "Endereço: 3, E-mail: 2" para os itens PLANEJADO (resumo) ou OK (concluído). */
  vcaCorrPorTipo(status: string): string {
    const cont = new Map<string, number>();
    for (const a of this.vcaCorrAcoes(status)) cont.set(a.tipo, (cont.get(a.tipo) || 0) + 1);
    return Array.from(cont.entries()).map(([t, n]) => `${t}: ${n}`).join(', ');
  }

  /** Clientes com algo a mostrar (no resumo, esconde os que não têm nada a fazer). */
  get vcaCorrExibir(): any[] {
    return this.vcaCorrLista.filter(c => (c.acoes || []).some((a: any) => a.status !== 'OK' || this.vcaCorrFase === 'concluido'));
  }

  vcaAbrir(r: any): void {
    this.vcaAberto = false;
    this.editarParceiro.emit({ doc: r.doc, codparc: (r.codparcs && r.codparcs[0]) || '' });
  }

  private vcaLinhasExport(): string[][] {
    return this.vcaFiltrados.map(r => [
      this.vcaRotulo(r.situacao),
      r.nome || '',
      r.cpfCnpj || '',
      r.resumo || '',
      (r.detalhes || []).join(' | '),
      (r.codparcs || []).join(', ')
    ]);
  }

  private readonly vcaColunas = ['Situação', 'Cadastro (Aerosys)', 'CPF/CNPJ', 'O que falta (resumo)', 'Detalhes', 'CODPARC no Sankhya'];

  vcaExportarExcel(): void {
    const ws = XLSX.utils.aoa_to_sheet([this.vcaColunas, ...this.vcaLinhasExport()]);
    ws['!cols'] = [16, 40, 20, 34, 100, 18].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cadastros Aerosys');
    XLSX.writeFile(wb, this.nomeArquivo('xlsx').replace('parceiros_', 'validacao_cadastros_aerosys_'));
  }

  vcaExportarCsv(): void {
    const fmt = (v: string): string => (/[;"\n\r]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v);
    const csv = [this.vcaColunas, ...this.vcaLinhasExport()].map(l => l.map(fmt).join(';')).join('\r\n');
    this.baixar(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }),
      this.nomeArquivo('csv').replace('parceiros_', 'validacao_cadastros_aerosys_'));
  }

  tipoPessoa(p: any): string {
    return p.tippessoa === 'F' ? 'Física' : p.tippessoa === 'J' ? 'Jurídica' : '—';
  }

  tipoCadastro(p: any): string {
    const m: Record<string, string> = { AVISTA: 'À Vista', APRAZO: 'À Prazo', ENTREGA: 'Entrega', RAPIDO: 'Rápido' };
    const v = String(p.gpl_tipocadastro || '').toUpperCase();
    return m[v] || v || '—';
  }

  documento(p: any): string {
    const d = String(p.cgc_cpf || '').replace(/\D/g, '');
    if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return d || '—';
  }
}
